<template>
  <div id="form-base" class="relative form-base">
    <div
      class="sticky top-0 z-10 flex items-center w-full h-12 px-6 lg:w-auto bg-slate-200"
    >
      <router-link to="/center/client-acquisition/form">
        <div
          class="flex items-center text-2xl font-bold font-logo text-light-purple"
        >
          Blüm
        </div>
      </router-link>
    </div>
    <div v-if="fetchLoading" class="grid form-body place-items-center">
      <div
        class="flex items-center text-4xl font-bold font-logo text-light-purple animate-pulse"
      >
        Blüm Form
      </div>
    </div>
    <div v-else-if="!parentForm" class="grid px-4 form-body place-items-center">
      <div
        class="flex flex-col items-center w-full gap-2 px-4 py-5 border-l-8 shadow-lg lg:w-148 lg:px-10 bg-pure-white border-red-cherry rounded-xl"
      >
        <BlTextTitle4 text="404: Form Not Found" />
        <BlTextBody2
          text="It looks like the form you're trying to reach doesn't exist or the URL is incorrect. Please check the URL."
          class="text-center"
        />
      </div>
    </div>
    <div v-else class="w-full">
      <div class="flex flex-col items-center w-full gap-5 px-4 py-4 lg:py-5">
        <div
          class="flex flex-col w-full gap-2 px-4 py-5 border-l-8 lg:w-148 lg:px-10 bg-pure-white border-light-purple-200 rounded-xl"
        >
          <BlTextTitle4 :text="parentForm.name" />
          <BlTextBody2 :text="parentForm.description" class="whitespace-pre-line text-slate-800" />
          <div class="flex">
            <div class="flex gap-1 px-1 rounded bg-tulip-100">
              <BlIcon icon="user-square" color="tulip-7" class="w-6 h-6" />
              <div>
                <BlTextBody2 text="This form is for " class="text-tulip-700" />
                <BlTextSubtitle3
                  :text="`${'Prospect Name'}'s`"
                  class="text-tulip-700"
                />
                <BlTextBody2
                  text=" guardian. Please submit it before "
                  class="text-tulip-700"
                />
                <BlTextSubtitle3
                  :text="`Expiration Date.`"
                  class="text-tulip-700"
                />
              </div>
            </div>
          </div>
        </div>
        <ProspectFormSection
          v-if="currentSection"
          :key="currentSection.id"
          :section="currentSection"
        />
      </div>
      <div
        v-if="currentSection"
        class="sticky bottom-0 left-0 flex flex-col w-screen h-32 gap-2 px-6 py-4 border-t lg:h-22 lg:flex-row lg:items-center lg:justify-between bg-pure-white border-slate-400"
      >
        <div>
          <div class="flex items-center gap-2 mb-2">
            <div
              v-for="section in parentFormSections"
              :key="section.id"
              class="relative flex-shrink-0 w-10 h-1 transition-all duration-300 rounded lg:w-32"
              :class="[
                currentSection.position >= section.position
                  ? 'bg-lime-400'
                  : 'bg-slate-400'
              ]"
            >
              <div
                class="absolute z-10 h-1 transition-all duration-300 rounded bg-lime-600"
                :style="{
                  width:
                    (getAnsweredCount(section.id) * 100) /
                      section.questions.length +
                    '%'
                }"
              ></div>
            </div>
          </div>
          <div class="mb-1">
            <BlTextSubtitle3
              :text="currentSection.name"
              class="text-slate-700"
            />
          </div>
          <div>
            <BlTextBody2
              :text="
                `Questions filled in this section: ${getAnsweredCount(
                  currentSection.id
                )}/${currentSection.questions.length}`
              "
            />
          </div>
        </div>
        <div class="flex flex-row items-center gap-4">
          <BlInputButton
            v-if="!isFirstSection"
            class="flex-grow"
            type="outlined"
            :dense="viewMode !== 'desktop'"
            label="Back"
            @clicked="onNextSection(-1)"
          />
          <BlInputButton
            v-if="!isLastSection"
            class="flex-grow"
            type="outlined"
            :dense="viewMode !== 'desktop'"
            label="Next"
            @clicked="onNextSection(1)"
          />
          <router-link
            v-if="isLastSection"
            to="/center/client-acquisition/form"
          >
            <BlInputButton
              class="flex-grow"
              label="Submit"
              :dense="viewMode !== 'desktop'"
              :loading="submitLoading"
            />
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import ProspectFormSection from "../../form/prospectForm/Section.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "ProspectFormPreview",
  components: {
    ProspectFormSection,
    BlTextTitle4,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlInputButton
  },
  data() {
    return {
      fetchLoading: false,
      saveLoading: false,
      submitLoading: false,
      currentSection: null
    };
  },
  computed: {
    ...mapGetters([
      "viewMode",
      "prospectForm",
      "parentForm",
      "parentFormSections",
      "prospectFormAnswers"
    ]),
    isFirstSection() {
      if (!this.currentSection) return false;
      if (!this.parentFormSections.length) return false;
      const index = this.parentFormSections.findIndex(
        i => i.id === this.currentSection.id
      );
      return index === 0;
    },
    isLastSection() {
      if (!this.currentSection) return false;
      if (!this.parentFormSections.length) return false;
      const index = this.parentFormSections.findIndex(
        i => i.id === this.currentSection.id
      );
      return index === this.parentFormSections.length - 1;
    }
  },
  mounted() {
    this.fetchParentForm();
  },
  destroyed() {
    const app = document.getElementById("app");
    if (app) app.scroll({ top: 0, left: 0 });
  },
  methods: {
    ...mapActions(["getParentForm"]),
    fetchParentForm() {
      this.fetchLoading = true;
      this.getParentForm().then(({ success, data }) => {
        if (!success) {
          this.fetchLoading = false;
          return;
        }
        this.currentSection = data.sections[0];
        this.fetchLoading = false;
      });
    },
    getAnsweredCount(sectionId) {
      const answered = this.prospectFormAnswers.filter(
        i => i.section_id === sectionId
      );
      return answered.length;
    },
    onNextSection(n) {
      const index = this.parentFormSections.findIndex(
        i => i.id === this.currentSection.id
      );
      const move = index + n;
      if (move < 0) return;
      if (move >= this.parentFormSections.length) return;
      this.currentSection = this.parentFormSections[move];
      const app = document.getElementById("app");
      if (app) {
        app.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }
};
</script>

<style scoped lang="scss">
.form-base {
  .form-body {
    height: calc(100vh - 0.75rem);
    width: calc(100vw - 0.5rem);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/center/salesPipeline/FormPreview.vue